// FabricationOrderAcceptanceHistoryDialog (#70, S36/CU5) — read-only history of every acceptance
// version of a fabrication order. Loads `GET /{id}/versions` on open and lists all attempts (newest
// first, as Sun returns them) with their attempt number, status, result and close date. Everything is
// presented read-only: there is no edit and no reopen action (RN-C/CA4) — closed versions are immutable.
//
// C8 (#70 r3, S50) — each row can be opened (the "eye") to inspect that specific attempt. The detail
// replaces the table inside the same dialog, with a "back to history" action: it shows the saved form
// values read-only (`GET /{id}/versions/{n}/form`, same sections as the live form) and, below them,
// that version's documents (S60) — the same `AcceptanceDocumentsList` section as the live form, but
// read-only (View/Download, no delete), fed by the version's documents already loaded from
// `GET /{id}/versions`.
import { useEffect, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Table,
  TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material'
import { ArrowBackRounded, VisibilityRounded } from '@mui/icons-material'
import { format, isValid } from 'date-fns'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { apiGet } from '../generic/Api_Functions'
import {
  getStatusLabelKey, getStatusChipProps, getResultLabelKey, getResultChipProps
} from './fabricationOrderAcceptanceStatus'
import FabricationOrderAcceptanceFormReadOnly from './FabricationOrderAcceptanceFormReadOnly'
import AcceptanceDocumentsList from './AcceptanceDocumentsList'

// Close date shown down to the second, without milliseconds (e.g. 2026-07-23 14:42:42), in the
// viewer's local time. Falls back to the raw value if it is not a parseable date, and to a dash
// when there is no close date (an open/paused version).
const formatClosedAt = (value) => {
  if (!value) return '—'
  const parsed = new Date(value)
  return isValid(parsed) ? format(parsed, 'yyyy-MM-dd HH:mm:ss') : value
}

/**
 * Props:
 *   fabricationOrderId — id whose version history is shown.
 *   open               — dialog open flag (controlled).
 *   onClose            — () => void, closes the dialog.
 */
export default function FabricationOrderAcceptanceHistoryDialog ({ fabricationOrderId, open, onClose }) {
  const { t_ } = useSmartTranslation()
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(false)
  // C8 — the attempt whose detail is being inspected (null = list mode), its form JSON and load state.
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [versionForm, setVersionForm] = useState(null)
  const [formLoading, setFormLoading] = useState(false)

  useEffect(() => {
    if (!open || !fabricationOrderId) return
    // Reset to list mode every time the dialog (re)opens.
    setSelectedVersion(null)
    setVersionForm(null)
    setLoading(true)
    apiGet(`fabricationOrderAcceptance/${fabricationOrderId}/versions`)
      .then((result) => setVersions(Array.isArray(result) ? result : []))
      .catch(() => setVersions([]))
      .finally(() => setLoading(false))
  }, [open, fabricationOrderId])

  const openVersionForm = (versionNumber) => {
    setSelectedVersion(versionNumber)
    setVersionForm(null)
    setFormLoading(true)
    apiGet(`fabricationOrderAcceptance/${fabricationOrderId}/versions/${versionNumber}/form`)
      .then((form) => setVersionForm(form && typeof form === 'object' ? form : {}))
      .catch(() => setVersionForm({}))
      .finally(() => setFormLoading(false))
  }

  const backToList = () => {
    setSelectedVersion(null)
    setVersionForm(null)
  }

  const isEmptyForm = versionForm && Object.keys(versionForm).length === 0

  // S60 — the documents of the attempt being inspected, taken from the already-loaded versions list
  // (GET /{id}/versions carries each version's non-deleted documents). Empty array = no documents.
  const selectedVersionData = versions.find((version) => version.versionNumber === selectedVersion)
  const selectedVersionDocuments = (selectedVersionData && selectedVersionData.documents) || []

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>
        {selectedVersion == null
          ? t_('Version history')
          : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size='small' color='primary' startIcon={<ArrowBackRounded />} onClick={backToList}>
                {t_('Back to history')}
              </Button>
              <span>{`${t_('Version')} ${selectedVersion}`}</span>
            </Box>
            )}
      </DialogTitle>
      <DialogContent data-testid='fabrication-order-acceptance-history'>
        {selectedVersion != null
          ? (
              formLoading
                ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                  )
                : (
                  <>
                    {isEmptyForm
                      ? (
                        <Typography sx={{ color: 'text.secondary', py: 2 }}>
                          {t_('No form data for this attempt.')}
                        </Typography>
                        )
                      : <FabricationOrderAcceptanceFormReadOnly form={versionForm} t_={t_} />}

                    {/* S60 — this version's documents, read-only (same section as the live form, no
                        delete). Rendered together with the form (only after it loads) so the documents
                        never pop in before the rest of the attempt's detail. */}
                    <AcceptanceDocumentsList fabricationOrderId={fabricationOrderId} documents={selectedVersionDocuments} />
                  </>
                  )
            )
          : (
            <>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                {t_('All acceptance attempts of this fabrication order, in read-only.')}
              </Typography>

              {loading
                ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                  )
                : versions.length === 0
                  ? (
                    <Typography sx={{ color: 'text.secondary', py: 2 }}>
                      {t_('No acceptance versions yet.')}
                    </Typography>
                    )
                  : (
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: 'text.secondary' }}>{t_('Version')}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{t_('Status')}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{t_('Result')}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{t_('Closed at')}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align='center'>{t_('Form')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {versions.map((version) => (
                          <TableRow key={version.versionNumber} data-testid={`acceptance-version-row-${version.versionNumber}`}>
                            <TableCell>{version.versionNumber}</TableCell>
                            <TableCell>
                              <Chip size='small' label={t_(getStatusLabelKey(version.status))} {...getStatusChipProps(version.status)} />
                            </TableCell>
                            <TableCell>
                              {getResultLabelKey(version.result)
                                ? <Chip size='small' label={t_(getResultLabelKey(version.result))} {...getResultChipProps(version.result)} />
                                : <Typography variant='body2' color='text.secondary'>—</Typography>}
                            </TableCell>
                            <TableCell>
                              <Typography variant='body2' color='text.secondary'>{formatClosedAt(version.closedAt)}</Typography>
                            </TableCell>
                            <TableCell align='center'>
                              <IconButton
                                size='small'
                                color='secondary'
                                aria-label={`${t_('View form')} ${version.versionNumber}`}
                                onClick={() => openVersionForm(version.versionNumber)}
                              >
                                <VisibilityRounded fontSize='small' />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    )}
            </>
            )}
      </DialogContent>
    </Dialog>
  )
}
